import {
  DocumentData,
  DocumentReference,
  Timestamp,
  arrayUnion,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, stringArrayField, stringField } from "./firestoreHelpers";

export type ModerationRowSource = "reports" | "flaggedContent" | "reportedMessages";
export type ModerationResolutionAction = "content_approved" | "removed_content" | "suspended_account";
export type ModerationCaseAction = "mark_reviewed" | "dismiss" | "remove_content" | "suspend_user";

export type ModerationTableRow = {
  id: string;
  contentId: string | null;
  contentType: string | null;
  relatedPostId: string | null;
  reportedUserId: string | null;
  sourceReportId: string | null;
  reportType: string;
  status: string;
  category: string;
  reportedTarget: string;
  reporter: string;
  createdAt: Date | null;
  source: ModerationRowSource;
};

export type ModerationAdminData = {
  pendingReports: ModerationTableRow[];
  pendingFlaggedContent: ModerationTableRow[];
  reportedMessages: ModerationTableRow[];
  reportedProfiles: ModerationTableRow[];
  reportedPosts: ModerationTableRow[];
  lastUpdated: Date;
};

const RECENT_LIMIT = 100;
const PENDING_STATUS = "pending";
const REVIEWED_STATUS = "reviewed";
const DISMISSED_STATUS = "dismissed";
const CONTENT_APPROVED: ModerationResolutionAction = "content_approved";
const REMOVED_CONTENT: ModerationResolutionAction = "removed_content";
const SUSPENDED_ACCOUNT: ModerationResolutionAction = "suspended_account";

type ReviewWrite = {
  reviewedBy: string;
  reviewedByEmail: string | null;
  resolutionNotes: string;
  status: string;
  resolutionAction: ModerationResolutionAction;
};

function fallback(value: string | null, unavailable = "Unavailable") {
  return value ?? unavailable;
}

function label(value: string | null) {
  if (!value) {
    return "Unavailable";
  }

  return value
    .split("_")
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function statusFrom(data: DocumentData, ...keys: string[]) {
  for (const key of keys) {
    const value = stringField(data, key);
    if (value) {
      return value;
    }
  }

  return PENDING_STATUS;
}

function targetFrom(type: string | null, contentId: string | null, reportedUserId: string | null, relatedPostId?: string | null) {
  const target = type === "profile" ? reportedUserId ?? contentId : contentId ?? reportedUserId;
  const base = `${label(type)}: ${fallback(target)}`;
  return relatedPostId ? `${base} / post ${relatedPostId}` : base;
}

async function recentDocs(collectionName: string, dateFieldName: string) {
  const snapshot = await getDocs(query(adminCollection(collectionName), orderBy(dateFieldName, "desc"), limit(RECENT_LIMIT)));
  return snapshot.docs.map((document) => ({ id: document.id, data: document.data() }));
}

function reportRow(id: string, data: DocumentData): ModerationTableRow {
  const targetType = stringField(data, "targetType");
  const contentId = stringField(data, "contentId");

  return {
    id,
    contentId,
    contentType: targetType,
    relatedPostId: stringField(data, "relatedPostId"),
    reportedUserId: stringField(data, "reportedUserId"),
    sourceReportId: id,
    reportType: label(targetType),
    status: statusFrom(data, "reviewStatus", "status"),
    category: label(stringField(data, "reason")),
    reportedTarget: targetFrom(targetType, contentId, stringField(data, "reportedUserId"), stringField(data, "relatedPostId")),
    reporter: fallback(stringField(data, "reporterId")),
    createdAt: dateField(data, "timestamp") ?? dateField(data, "createdAt"),
    source: COLLECTIONS.reports
  };
}

function flaggedContentRow(id: string, data: DocumentData, reportRowsById: Map<string, ModerationTableRow>): ModerationTableRow {
  const reasons = stringArrayField(data, "reasons");
  const sourceReportId = stringField(data, "sourceReportId");
  const sourceReport = sourceReportId ? reportRowsById.get(sourceReportId) : null;
  const contentType = stringField(data, "contentType");
  const contentId = stringField(data, "contentId");

  return {
    id,
    contentId,
    contentType,
    relatedPostId: stringField(data, "relatedPostId"),
    reportedUserId: stringField(data, "userId"),
    sourceReportId,
    reportType: label(contentType),
    status: statusFrom(data, "status", "reviewStatus"),
    category: reasons.length > 0 ? reasons.map((reason) => label(reason)).join(", ") : label(stringField(data, "flagType")),
    reportedTarget: targetFrom(contentType, contentId, stringField(data, "userId"), stringField(data, "relatedPostId")),
    reporter: sourceReport?.reporter ?? fallback(stringField(data, "flagSource"), "System"),
    createdAt: dateField(data, "createdAt") ?? dateField(data, "updatedAt"),
    source: COLLECTIONS.flaggedContent
  };
}

function reportedMessageRow(id: string, data: DocumentData, reportRowsById: Map<string, ModerationTableRow>): ModerationTableRow {
  const reportId = stringField(data, "reportId") ?? id;
  const sourceReport = reportRowsById.get(reportId);

  return {
    id,
    contentId: stringField(data, "conversationId"),
    contentType: "conversation",
    relatedPostId: null,
    reportedUserId: stringField(data, "reportedUserId"),
    sourceReportId: reportId,
    reportType: "Conversation",
    status: sourceReport?.status ?? PENDING_STATUS,
    category: sourceReport?.category ?? label(stringField(data, "reason")),
    reportedTarget: targetFrom("conversation", stringField(data, "conversationId"), stringField(data, "reportedUserId")),
    reporter: sourceReport?.reporter ?? fallback(stringField(data, "reporterId")),
    createdAt: dateField(data, "createdAt") ?? sourceReport?.createdAt ?? null,
    source: COLLECTIONS.reportedMessages
  };
}

export async function fetchModerationAdminData(): Promise<ModerationAdminData> {
  const [reportDocs, flaggedContentDocs, reportedMessageDocs] = await Promise.all([
    recentDocs(COLLECTIONS.reports, "timestamp"),
    recentDocs(COLLECTIONS.flaggedContent, "updatedAt"),
    recentDocs(COLLECTIONS.reportedMessages, "createdAt")
  ]);

  const reportRows = reportDocs.map((document) => reportRow(document.id, document.data));
  const reportRowsById = new Map(reportRows.map((row) => [row.id, row]));
  const flaggedContentRows = flaggedContentDocs.map((document) => flaggedContentRow(document.id, document.data, reportRowsById));

  return {
    pendingReports: reportRows.filter((row) => row.status === PENDING_STATUS),
    pendingFlaggedContent: flaggedContentRows.filter((row) => row.status === PENDING_STATUS),
    reportedMessages: reportedMessageDocs.map((document) => reportedMessageRow(document.id, document.data, reportRowsById)),
    reportedProfiles: reportRows.filter((row) => row.reportType === "Profile"),
    reportedPosts: reportRows.filter((row) => row.reportType === "Post"),
    lastUpdated: new Date()
  };
}

function reviewFields(write: ReviewWrite, statusField: "status" | "reviewStatus") {
  const auditEntry = {
    action: write.resolutionAction,
    status: write.status,
    reviewedBy: write.reviewedBy,
    reviewedByEmail: write.reviewedByEmail,
    resolutionNotes: write.resolutionNotes,
    createdAt: Timestamp.now()
  };

  return {
    [statusField]: write.status,
    reviewedBy: write.reviewedBy,
    reviewedByEmail: write.reviewedByEmail,
    reviewedAt: serverTimestamp(),
    resolutionAction: write.resolutionAction,
    resolutionNotes: write.resolutionNotes,
    updatedAt: serverTimestamp(),
    auditHistory: arrayUnion(auditEntry)
  };
}

function sourceReference(row: ModerationTableRow): DocumentReference {
  return doc(db, row.source, row.id);
}

function queueReference(row: ModerationTableRow): DocumentReference | null {
  if (row.source === COLLECTIONS.flaggedContent) {
    return sourceReference(row);
  }

  if (!row.contentType || !row.contentId) {
    return null;
  }

  return doc(db, COLLECTIONS.flaggedContent, `${row.contentType}_${row.contentId}`);
}

function reportReference(row: ModerationTableRow): DocumentReference | null {
  if (row.source === COLLECTIONS.reports) {
    return sourceReference(row);
  }

  return row.sourceReportId ? doc(db, COLLECTIONS.reports, row.sourceReportId) : null;
}

function softHideFields(write: ReviewWrite) {
  return {
    isHidden: true,
    hiddenAt: serverTimestamp(),
    hiddenBy: write.reviewedBy,
    hiddenByEmail: write.reviewedByEmail,
    hiddenReason: "moderation",
    moderationStatus: "hidden",
    moderationResolutionAction: write.resolutionAction,
    moderationResolutionNotes: write.resolutionNotes,
    updatedAt: serverTimestamp()
  };
}

function contentReference(row: ModerationTableRow): DocumentReference | null {
  if (!row.contentType || !row.contentId) {
    return null;
  }

  switch (row.contentType) {
    case "profile":
      return doc(db, COLLECTIONS.users, row.reportedUserId ?? row.contentId);
    case "post":
      return doc(db, COLLECTIONS.posts, row.contentId);
    case "comment":
      return row.relatedPostId ? doc(db, COLLECTIONS.posts, row.relatedPostId, "comments", row.contentId) : null;
    case "opportunity":
      return doc(db, COLLECTIONS.opportunities, row.contentId);
    case "conversation":
      return doc(db, COLLECTIONS.conversations, row.contentId);
    case "challenge":
      return doc(db, COLLECTIONS.challenges, row.contentId);
    case "post_feedback":
      return doc(db, COLLECTIONS.postFeedback, row.contentId);
    default:
      return null;
  }
}

function contentSoftHideFields(row: ModerationTableRow, write: ReviewWrite) {
  const fields: Record<string, unknown> = softHideFields(write);

  if (row.contentType === "challenge") {
    fields.status = "removed";
    fields.removedAt = serverTimestamp();
  }

  if (row.contentType === "opportunity") {
    fields.status = "archived";
    fields.visibility = "private";
  }

  if (row.contentType === "profile") {
    fields.discoverabilityStatus = "hidden";
  }

  return fields;
}

async function updateIfExists(
  batch: ReturnType<typeof writeBatch>,
  reference: DocumentReference | null,
  data: Record<string, unknown>,
  writtenPaths: Set<string>
) {
  if (!reference || writtenPaths.has(reference.path)) {
    return;
  }

  const snapshot = await getDoc(reference);
  if (snapshot.exists()) {
    batch.update(reference, data);
    writtenPaths.add(reference.path);
  }
}

function writeFromAction(action: ModerationCaseAction, reviewedBy: string, reviewedByEmail: string | null, resolutionNotes: string): ReviewWrite {
  if (action === "dismiss") {
    return {
      reviewedBy,
      reviewedByEmail,
      resolutionNotes,
      status: DISMISSED_STATUS,
      resolutionAction: CONTENT_APPROVED
    };
  }

  if (action === "remove_content") {
    return {
      reviewedBy,
      reviewedByEmail,
      resolutionNotes,
      status: REVIEWED_STATUS,
      resolutionAction: REMOVED_CONTENT
    };
  }

  if (action === "suspend_user") {
    return {
      reviewedBy,
      reviewedByEmail,
      resolutionNotes,
      status: REVIEWED_STATUS,
      resolutionAction: SUSPENDED_ACCOUNT
    };
  }

  return {
    reviewedBy,
    reviewedByEmail,
    resolutionNotes,
    status: REVIEWED_STATUS,
    resolutionAction: CONTENT_APPROVED
  };
}

export async function resolveModerationCase({
  action,
  reviewedBy,
  reviewedByEmail,
  resolutionNotes,
  row
}: {
  action: ModerationCaseAction;
  reviewedBy: string;
  reviewedByEmail: string | null;
  resolutionNotes: string;
  row: ModerationTableRow;
}) {
  const write = writeFromAction(action, reviewedBy, reviewedByEmail, resolutionNotes.trim());
  const batch = writeBatch(db);
  const sourceStatusField = row.source === COLLECTIONS.reports ? "reviewStatus" : "status";
  const writtenPaths = new Set<string>();
  const sourceRef = sourceReference(row);

  batch.update(sourceRef, reviewFields(write, sourceStatusField));
  writtenPaths.add(sourceRef.path);
  await updateIfExists(batch, reportReference(row), reviewFields(write, "reviewStatus"), writtenPaths);
  await updateIfExists(batch, queueReference(row), reviewFields(write, "status"), writtenPaths);

  if (action === "remove_content") {
    await updateIfExists(batch, contentReference(row), contentSoftHideFields(row, write), writtenPaths);
  }

  if (action === "suspend_user" && row.reportedUserId) {
    batch.update(doc(db, COLLECTIONS.users, row.reportedUserId), {
      isSuspended: true,
      suspendedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }

  await batch.commit();
}
